package com.sddamico.draftmachine.ds.overlays

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.displayInlineBlock
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.motion.LocalExiting
import com.sddamico.draftmachine.ds.motion.Presence
import com.sddamico.draftmachine.ds.roleSmall
import com.sddamico.draftmachine.ds.shadowRaised
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as DomButton
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/** Which edge the dropdown aligns to under its trigger. */
enum class MenuAlign { Left, Right }

/** `Rust` marks a destructive item (rust text + icon). */
enum class MenuItemTone { Rust }

/**
 * One entry in a [Menu]. Set [divider] `true` (or use [MenuDivider]) for a hairline separator;
 * such entries ignore every other field.
 *
 * @param hint a mono micro-label shown right-aligned, e.g. a keyboard shortcut.
 */
data class MenuItem(
  val label: String? = null,
  val icon: String? = null,
  val tone: MenuItemTone? = null,
  val hint: String? = null,
  val disabled: Boolean = false,
  val onSelect: (() -> Unit)? = null,
  val divider: Boolean = false,
)

/** Convenience separator entry, mirroring the JSX `'divider'` string form. */
val MenuDivider: MenuItem = MenuItem(divider = true)

/**
 * Base transparent surface + hover wash for a menu row. Public so Kobweb's KSP processor
 * auto-registers it (a private CssStyle is never emitted to the stylesheet, so its hover rule
 * would silently do nothing) — same reason [com.sddamico.draftmachine.ds.PressableStyle] is public.
 */
val MenuItemStyle = CssStyle {
  base { Modifier.backgroundColor(Colors.Transparent) }
  hover { Modifier.backgroundColor(DmColor.surfaceHover) }
}

/**
 * A dropdown of stamped actions behind a [trigger] — row actions, overflow "..." menus. Closes on
 * outside click, Escape, or item selection. Port of `components/overlays/Menu.jsx`.
 *
 * @param modifier applied to the relatively-positioned wrapper (mirrors the JSX `style` spread).
 * @param trigger the element that toggles the menu (a Button, IconButton…).
 */
@Composable
fun Menu(
  trigger: @Composable () -> Unit,
  modifier: Modifier = Modifier,
  items: List<MenuItem> = emptyList(),
  align: MenuAlign = MenuAlign.Left,
  defaultOpen: Boolean = false,
) {
  var open by remember { mutableStateOf(defaultOpen) }
  val rootHolder = remember { arrayOfNulls<HTMLElement>(1) }

  if (open) {
    DisposableEffect(Unit) {
      val onDown: (Event) -> Unit = { e ->
        val target = e.target as? Node
        val root = rootHolder[0]
        if (root != null && (target == null || !root.contains(target))) open = false
      }
      val onKey: (Event) -> Unit = { e ->
        if ((e as? KeyboardEvent)?.key == "Escape") open = false
      }
      document.addEventListener("mousedown", onDown)
      document.addEventListener("keydown", onKey)
      onDispose {
        document.removeEventListener("mousedown", onDown)
        document.removeEventListener("keydown", onKey)
      }
    }
  }

  Div(
    attrs = Modifier
      .displayInlineBlock()
      .styleModifier { property("position", "relative") }
      .then(modifier)
      .toAttrs { ref { el -> rootHolder[0] = el; onDispose { rootHolder[0] = null } } }
  ) {
    Div(attrs = Modifier.displayInlineFlex().toAttrs { onClick { open = !open } }) {
      trigger()
    }
    Presence(open, exitMillis = 100) {
      val exiting = LocalExiting.current
      Div(
        attrs = Modifier
          .displayFlex()
          .flexDirection(FlexDirection.Column)
          .gap(2.px)
          .minWidth(220.px)
          .padding(DmSpacing.space2)
          .backgroundColor(DmColor.surfaceRaised)
          .border(1.px, LineStyle.Solid, DmColor.borderStrong)
          .borderRadius(DmEffects.radiusMd)
          .shadowRaised()
          .styleModifier {
            property("position", "absolute")
            property("top", "calc(100% + 6px)")
            if (align == MenuAlign.Right) property("right", "0") else property("left", "0")
            property("z-index", "50")
            property("transform-origin", if (align == MenuAlign.Right) "top right" else "top left")
            property(
              "animation",
              if (exiting) "dm-fade-out 100ms ${DmMotion.easeOut} both"
              else "dm-menu-in 180ms ${DmMotion.easeSnap} both",
            )
            if (exiting) property("pointer-events", "none")
          }
          .toAttrs { attr("role", "menu") }
      ) {
        items.forEach { item ->
          if (item.divider) {
            Div(
              attrs = Modifier
                .height(1.px)
                .backgroundColor(DmColor.border)
                .styleModifier { property("margin", "${DmSpacing.space1} ${DmSpacing.space2}") }
                .toAttrs()
            ) {}
          } else {
            MenuItemRow(item) { open = false }
          }
        }
      }
    }
  }
}

/** One stamped action row. Rust tone recolors text + icon; disabled rows dim and skip the hover wash. */
@Composable
private fun MenuItemRow(item: MenuItem, onClose: () -> Unit) {
  val isRust = item.tone == MenuItemTone.Rust
  val textColor = if (isRust) DmColor.rust400 else DmColor.text
  val iconColor = if (isRust) DmColor.rust400 else DmColor.textMuted
  val surface = if (item.disabled) Modifier.backgroundColor(Colors.Transparent) else MenuItemStyle.toModifier()

  DomButton(
    attrs = surface
      .displayFlex()
      .alignItems(AlignItems.Center)
      .gap(DmSpacing.space3)
      .padding(topBottom = DmSpacing.space2, leftRight = DmSpacing.space3)
      .borderRadius(DmEffects.radiusSm)
      .roleSmall()
      .color(textColor)
      .opacity(if (item.disabled) 0.45 else 1.0)
      .cursor(if (item.disabled) Cursor.NotAllowed else Cursor.Pointer)
      .whiteSpace(WhiteSpace.NoWrap)
      .styleModifier { property("border", "none"); property("text-align", "left") }
      .toAttrs {
        attr("role", "menuitem")
        if (item.disabled) attr("disabled", "")
        onClick { onClose(); item.onSelect?.invoke() }
      }
  ) {
    if (item.icon != null) {
      Icon(item.icon, IconWeight.Bold, Modifier.fontSize(16.px).color(iconColor))
    }
    SpanText(item.label ?: "", Modifier.flex("1"))
    if (item.hint != null) {
      SpanText(
        item.hint,
        Modifier
          .fontFamily(*DmType.mono)
          .fontSize(DmType.text2xs)
          .color(DmColor.textMuted)
          .letterSpacing(DmType.trackingWide)
      )
    }
  }
}
